import { useEffect, useState } from "react";

import type { CarrierProviderAddOnService } from "@/api/carrier";
import { PickupServiceEnablement } from "@/features/pickup-service-enablement";
import { PickupServiceOverview } from "@/features/pickup-service-overview";
import { PickupServiceRates } from "@/features/pickup-service-rates";
import { PickupServiceSlots } from "@/features/pickup-service-slots";

/**
 * A pickup service's whole configuration, on one page.
 *
 * This was a route of its own with a four-tab bar, reached by clicking the
 * single row most carriers have. That paid for navigation twice — a list to
 * reach one row, then tabs to reach one panel — on a screen configured once
 * and revisited rarely. The panels are sections now; the reader scrolls.
 *
 * The unsaved-changes guard moves with them: an editor that has been touched
 * warns before the page is left.
 *
 * Switching to a different service must not carry a draft across. That is the
 * caller's `key={service.id}` rather than an effect here — remounting is how
 * React resets state on an identity change, and an effect that only calls
 * setState reads to a linter as a dependency that could be dropped.
 */
export function PickupServiceConfig({
  service,
  onDirtyChange,
}: {
  service: CarrierProviderAddOnService;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [dirty, setDirty] = useState(false);

  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  return (
    <div className="space-y-4">
      <PickupServiceOverview
        key={`overview-${service.id}`}
        service={service}
        onDirtyChange={setDirty}
      />
      <PickupServiceRates
        key={`rates-${service.id}`}
        service={service}
        onDirtyChange={setDirty}
      />
      <PickupServiceSlots service={service} />
      <PickupServiceEnablement serviceId={service.id} />
    </div>
  );
}
